import { DetailCard, DetailForm, LayoutTable } from 'xui-pro';
import { Image, message, Tabs, Row, Col, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { useMemo, useState, useRef } from 'react';
import api from '@/api';
import { useFormItems, PHOTOTYPE, useColumns } from './useConfig';
import type { Part } from '@/api/epc/catalog/usageUsingPOST';
import type { PartListPhotoResp } from '@/api/epc/part/partPhotoUsingPOST';
import type { SubstituteItem } from '@/api/epc/part/partSubstituteUsingPOST';
import ImagePreview from '@/components/ImagePreview';
import { getAppRootDom } from '@/App';
import useParentAutoHeight from '@/hooks/useParentAutoHeight';
import { RichTextViewer } from '@xpeng/xdragon-ass-components-eu';

import styles from './index.module.less';

type PartDetailProps = {
  onClose: () => void;
  initialValues: Part;
  handlePartSearch: (partNo: string) => void;
  activeKey: string;
  setActiveKey: React.Dispatch<React.SetStateAction<string>>;
  isInIframe: boolean;
};

const PartDetail = (props: PartDetailProps) => {
  const { onClose, initialValues, handlePartSearch, activeKey, setActiveKey, isInIframe } = props;
  const formItems = useFormItems();
  const columns = useColumns(handlePartSearch, isInIframe);
  const partNo = useMemo(() => initialValues.partNo, [initialValues]);
  const [photoList, setPhotoList] = useState<PartListPhotoResp[]>([]);
  const [substituteList, setSubstituteList] = useState<SubstituteItem[]>([]);
  const [specialRemark, setSpecialRemark] = useState<string>('');
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const partDetailRef = useRef<HTMLDivElement>(null);

  const { height } = useParentAutoHeight({
    parentContainerRef: partDetailRef,
    containerRef: tabContainerRef,
    placeholderHeight: 96,
    dependency: initialValues?.partNo
  });

  const { loading: photoLoading } = useRequest(
    async () => {
      const res = await api.epc.part.photo({ partNo });
      if (res.code === 200) {
        setPhotoList(res?.data ?? []);
      } else message.error(res.msg);
    },
    { manual: false, refreshDeps: [partNo] }
  );
  const { loading: substituteLoading } = useRequest(
    async () => {
      const res = await api.epc.part.substitute({ partNo });
      if (res.code === 200) {
        setSubstituteList(res?.data?.substituteList ?? []);
      } else message.error(res.msg);
    },
    { manual: false, refreshDeps: [partNo] }
  );
  useRequest(
    async () => {
      const res = await api.epc.part.specialRemark({ partNo });
      if (res.code === 200) {
        setSpecialRemark(res?.data?.content ?? '');
      } else message.error(res.msg);
    },
    { manual: false, refreshDeps: [partNo] }
  );

  return (
    <div ref={partDetailRef} style={{ height: '100%' }}>
      <DetailCard
        title='备件详情'
        className={styles.partDetail}
        action={<CloseOutlined onClick={onClose} />}
      >
        <DetailForm
          initialValues={initialValues}
          formItems={formItems}
          span={12}
          hasEmptyPlaceholder={true}
          emptyPlaceholder='-'
        />
        <div ref={tabContainerRef} className={styles.tabContainer}>
          <Tabs
            defaultActiveKey='1'
            activeKey={activeKey}
            onChange={key => setActiveKey(key)}
            destroyInactiveTabPane
          >
            <Tabs.TabPane tab='照片' key='1'>
              <Spin spinning={photoLoading}>
                <Image.PreviewGroup preview={{ getContainer: getAppRootDom }}>
                  <Row gutter={[16, 16]} wrap justify='space-between'>
                    {!photoList.length ? (
                      <Col span={12}>
                        <div className={styles.noPhoto}>
                          <img src={require('../assets/partPic.png')} alt='' />
                          <span>暂无图片</span>
                        </div>
                      </Col>
                    ) : (
                      photoList?.map(({ fileId, photoType }) => {
                        if (fileId)
                          return (
                            <Col span={12} className={styles.photoItem} key={fileId}>
                              <ImagePreview fileId={fileId} />
                              <span>{PHOTOTYPE[String(photoType)]}</span>
                            </Col>
                          );
                      })
                    )}
                  </Row>
                </Image.PreviewGroup>
              </Spin>
            </Tabs.TabPane>
            <Tabs.TabPane tab='替换件' key='2'>
              <LayoutTable
                columns={columns}
                loading={substituteLoading}
                dataSource={substituteList}
                hasToolbar={false}
                pagination={false}
                hasAutoHeight={false}
                height={height}
                emptyHeight={height - 30}
                hasEmptyPlaceholder={true}
                emptyPlaceholder='-'
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab='特别信息' key='3'>
              <RichTextViewer value={specialRemark || '无'} getContainer={getAppRootDom} />
            </Tabs.TabPane>
          </Tabs>
        </div>
      </DetailCard>
    </div>
  );
};

export default PartDetail;
